import { Icon, type IconName } from '@/shared/assets/icons';
import { useDebounce } from '@/shared/lib/hooks';
import type { ComponentProps } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { Input } from './input';
import { Loader } from './loader';

type BaseInputProps = ComponentProps<typeof Input>;

export type SearchBoxProps = {
  loading?: boolean;
  onChange?: (value: string) => void;
  debounceDelay?: number;
  iconName?: IconName;
} & Omit<BaseInputProps, 'rightNode' | 'onChange'>;

export const SearchBox = forwardRef<HTMLInputElement, SearchBoxProps>(
  (
    {
      loading,
      className,
      defaultValue,
      disabled,
      debounceDelay = 500,
      iconName = 'search',
      onChange: onChangeCallback,
      value: controlledValue,
      ...rest
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useState<string>(
      String(controlledValue ?? defaultValue ?? '')
    );
    const debouncedValue = useDebounce(innerValue, debounceDelay);

    useEffect(() => {
      if (controlledValue !== undefined) {
        setInnerValue(String(controlledValue));
      }
    }, [controlledValue]);

    useEffect(() => {
      onChangeCallback?.(debouncedValue);
    }, [debouncedValue]);

    const rightNode = loading ? (
      <Loader className="text-neutral-7" size={14} />
    ) : (
      <Icon className="text-neutral-7" size={14} name={iconName} />
    );

    return (
      <div className="flex">
        <Input
          ref={ref}
          disabled={disabled}
          bottomGutter={false}
          className={className}
          style={{ height: '40px' }}
          value={controlledValue !== undefined ? controlledValue : innerValue}
          onChange={(e) => {
            const next = e.currentTarget.value;
            setInnerValue(next);
          }}
          {...rest}
        />
        <div className="border-neutral-5 h-10 w-10 border p-[13px]">
          {rightNode}
        </div>
      </div>
    );
  }
);

SearchBox.displayName = 'SearchBox';
